<script setup>
import { computed } from 'vue';
import { dateRanges } from '../helpers/DatePickerHelper';
import { format, isSameYear, isValid } from 'date-fns';
import { useI18n } from 'dashboard/composables/useI18n';
import { getDateFnsLocale } from 'shared/helpers/timeHelper';

const props = defineProps({
  selectedStartDate: Date,
  selectedEndDate: Date,
  selectedRange: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['open']);
const { locale, t } = useI18n();

const formatDateRange = computed(() => {
  const startDate = props.selectedStartDate;
  const endDate = props.selectedEndDate;
  const dateLocale = getDateFnsLocale(locale.value);

  if (!isValid(startDate) || !isValid(endDate)) {
    return t('DATE_PICKER.SELECT_DATE_RANGE');
  }

  const formatString = isSameYear(startDate, endDate)
    ? 'MMM d' // Same year: "Apr 1"
    : 'MMM d yyyy'; // Different years: "Apr 1 2025"

  if (isSameYear(startDate, new Date()) && isSameYear(endDate, new Date())) {
    // Both dates are in the current year
    return `${format(startDate, 'MMM d', { locale: dateLocale })} - ${format(
      endDate,
      'MMM d',
      { locale: dateLocale }
    )}`;
  }
  // At least one date is not in the current year
  return `${format(startDate, formatString, {
    locale: dateLocale,
  })} - ${format(endDate, formatString, { locale: dateLocale })}`;
});

const activeDateRange = computed(
  () => dateRanges.find(range => range.value === props.selectedRange).label
);

const openDatePicker = () => {
  emit('open');
};
</script>

<template>
  <button
    class="inline-flex relative items-center rounded-lg gap-2 py-1.5 px-3 h-8 bg-surface-sunken hover:bg-surface-hover active:bg-line-faint"
    @click="openDatePicker"
  >
    <fluent-icon class="text-body" icon="calendar" size="16" />
    <span class="text-sm font-medium text-body">
      {{ $t(activeDateRange) }}
    </span>
    <span class="text-sm font-medium text-muted">
      {{ formatDateRange }}
    </span>
    <fluent-icon class="text-body" icon="chevron-down" size="14" />
  </button>
</template>
